import type { BarElement, Chart, Plugin } from 'chart.js';

const BAR_FILL_ALPHA_STOPS: Array<{ offset: number; alpha: string }> = [
  { offset: 0, alpha: '05' },
  { offset: 0.5, alpha: '50' },
  { offset: 1, alpha: '99' },
];

export type VerticalBarGradientPluginOptions = {
  color: string | readonly string[];
};

function applyBarFillColorStops(gradient: CanvasGradient, color: string): void {
  for (const stop of BAR_FILL_ALPHA_STOPS) {
    gradient.addColorStop(stop.offset, `${color}${stop.alpha}`);
  }
}

export function createVerticalBarCanvasGradient(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  base: number,
  color: string,
): CanvasGradient {
  const gradient = ctx.createLinearGradient(x, base, x, y);
  applyBarFillColorStops(gradient, color);
  return gradient;
}

function resolveBarFill(
  ctx: CanvasRenderingContext2D,
  bar: BarElement,
  value: unknown,
  color: string,
): string | CanvasGradient {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'transparent';
  }

  const { x, y, base } = bar.getProps(['x', 'y', 'base'], true);
  if (![x, y, base].every((coord) => typeof coord === 'number' && Number.isFinite(coord))) {
    return `${color}99`;
  }

  return createVerticalBarCanvasGradient(ctx, x, y, base, color);
}

/** Paint gradients onto each bar element after layout (dataset-level colors are resolved too early). */
export function applyVerticalBarGradientFills(chart: Chart<'bar'>, color: string | readonly string[]): void {
  const { ctx } = chart;
  if (!ctx) {
    return;
  }

  chart.data.datasets.forEach((dataset, datasetIndex) => {
    const datasetColor = typeof color === 'string' ? color : color[datasetIndex % color.length]!;
    const meta = chart.getDatasetMeta(datasetIndex);
    if (meta.type !== 'bar') {
      return;
    }

    meta.data.forEach((element, dataIndex) => {
      const bar = element as BarElement;
      const sharedOptions = bar.options as typeof bar.options & {
        hoverBackgroundColor?: string | CanvasGradient;
        $shared?: boolean;
      };
      const barOptions = sharedOptions.$shared
        ? ({ ...sharedOptions, $shared: false } as typeof sharedOptions)
        : sharedOptions;
      if (barOptions !== sharedOptions) {
        bar.options = barOptions;
      }
      const fill = resolveBarFill(ctx, bar, dataset.data[dataIndex], datasetColor);
      barOptions.backgroundColor = fill;
      barOptions.hoverBackgroundColor = fill;
    });
  });
}

function hasGradientColors(color: string | readonly string[] | undefined): color is string | readonly string[] {
  if (color == null) {
    return false;
  }

  return typeof color === 'string' || color.length > 0;
}

export const verticalBarGradientPlugin: Plugin<'bar'> = {
  id: 'verticalBarGradient',
  afterDatasetsUpdate(chart, _args, options) {
    const color = (options as VerticalBarGradientPluginOptions | undefined)?.color;
    if (!hasGradientColors(color)) {
      return;
    }

    applyVerticalBarGradientFills(chart, color);
  },
  beforeDatasetsDraw(chart, _args, options) {
    const color = (options as VerticalBarGradientPluginOptions | undefined)?.color;
    if (!hasGradientColors(color)) {
      return;
    }

    applyVerticalBarGradientFills(chart, color);
  },
};

/** Mirrors the report horizontal-bar gradient (chart.utils.tsx) for CSS-rendered bars. */
export function barFillGradient(color: string, direction: 'horizontal' | 'vertical'): string {
  const angle = direction === 'horizontal' ? '90deg' : '0deg';
  const stops = BAR_FILL_ALPHA_STOPS.map((stop) => `${color}${stop.alpha} ${stop.offset * 100}%`).join(', ');
  return `linear-gradient(${angle}, ${stops})`;
}
